<template>
  <b-modal
    id="duplicate-dataset-modal"
    v-model="openModal"
    size="md"
    :title="$t('modals.duplicateDataset.title')"
  >
    <b-overlay
      :show="loading"
      rounded="lg"
      opacity="0.6"
    >
      <label for="duplicate-dataset-title">
        {{ $t('forms.datasets.name.label') }}
      </label>
      <b-form-input
        id="duplicate-dataset-title"
        v-model="displayName"
        :placeholder="$t('forms.datasets.name.placeholder')"
      />
    </b-overlay>
    <template #modal-footer>
      <div class="w-100">
        <b-button
          variant="outline-secondary"
          class="float-left"
          :disabled="loading"
          @click="closeModal"
        >
          {{ $t('buttons.cancel') }}
        </b-button>
        <b-button
          variant="primary"
          class="float-right"
          :disabled="!displayName || !displayName.trim() || loading"
          @click="confirmDuplicate"
        >
          {{ $t('buttons.duplicate') }}
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import { mapState, mapActions } from 'vuex';

import datasetAPI from '@/api/modules/dataset.api.js';
import { ErrorService } from '@/services/error-service.js';
import i18n from '@/common/i18n/i18n.js';
import axios from 'axios';
import { slugify } from '@/utils';

export default {
  name: 'DuplicateDatasetModal',

  props: {
    dataset: {
      type: Object,
      default: () => {
        return {};
      }
    }
  },

  data() {
    return {
      openModal: true,
      loading: false,
      displayName: ''
    };
  },
  async mounted() {
    if(this.dataset && this.dataset.id) {
      this.loading = true;
      await this.GET_DATASET_DETAIL(this.dataset.id);
      await this.GET_CURRENT_DATASET_ROLES(this.dataset.id);
      this.loading = false;
   }
    this.displayName = this.currentDataset.display_name.concat(' ', '(copie)');
  },
  computed: {
    ...mapState('datasets', [
      'currentDataset',
      'currentDatasetRoles'
    ])
  },


  methods: {
    ...mapActions('datasets', [
      'GET_DATASET_DETAIL',
      'GET_CURRENT_DATASET_ROLES',
    ]),
    resetModal() {
      this.openModal = false;
      this.loading = false;
      this.displayName = '';
    },

    closeModal() {
      this.resetModal();
      this.$emit('hide');
    },

    async confirmDuplicate() {
      const trimmedDisplayName = this.displayName.trim();
      if (!trimmedDisplayName) {
        return;
      }

      try {
        this.loading = true;
        const parentId = this.currentDataset.parent || null;
        const recordTypeValue = this.currentDataset.record_type
          ? this.currentDataset.record_type.value
          : null;
        const extras = (this.currentDataset.extras || [])
          .filter((extra) => extra.key && extra.key !== 'ckan_id')
          .map(({ key, value }) => ({ key, value }));

        const newdataset = {
          ...this.currentDataset,
          codename: slugify(trimmedDisplayName).substring(0, 100),
          display_name: trimmedDisplayName,
          description: this.currentDataset.description,
          usergroup: this.currentDataset.usergroup.id,
          thumbnail: this.currentDataset.thumbnail,
          record_type_value: recordTypeValue,
          extras,
        };
        delete newdataset.id;
        delete newdataset.parent;
        delete newdataset.record_type;
        delete newdataset.mainResource;
        delete newdataset.resource_main;
        delete newdataset.resource_annexes;
        delete newdataset.children;
        delete newdataset._processing;
        delete newdataset._perms;

        const thumbnail = this.currentDataset.thumbnail;
        const createdDataset = await datasetAPI.createDataset(newdataset);
        if (!createdDataset) {
          return;
        }

        if (parentId) {
          try {
            await datasetAPI.patchDataset(createdDataset.id, { parent: parentId });
          } catch (parentErr) {
            ErrorService.onError(parentErr);
          }
        }

        let datasetThumbnailok = true;
        if (thumbnail) {
          datasetThumbnailok = false;
          try {
            if (thumbnail instanceof FormData) {
              datasetThumbnailok = await datasetAPI.setDatasetThumbnail(createdDataset.id, thumbnail);
            } else if (thumbnail && thumbnail.url) {
              const blobresponse = await axios.get(new URL(thumbnail.url, window.location.origin), { responseType: 'blob' });
              if (blobresponse && blobresponse.status === 200) {
                const formData = new FormData();
                const name = thumbnail.name.split('/')[1];
                formData.append('file', blobresponse.data, name);
                datasetThumbnailok = await datasetAPI.setDatasetThumbnail(createdDataset.id, formData);
              }
            }
          } catch (err) {
            ErrorService.onError(false, i18n.messages[i18n.locale].modals.duplicateDataset.error.thumbnail);
          }
        }

        for (const role of this.currentDatasetRoles || []) {
          await datasetAPI.createDatasetRole(createdDataset.id, {
            role: role.role,
            contact_id: role.contact.id,
            main: role.main
          });
        }
        if (createdDataset && datasetThumbnailok) {
          ErrorService.onSuccess(
            createdDataset,
            i18n.messages[i18n.locale].messages.success.dataset.created
              .replace('{dataset}', createdDataset.display_name)
          );
          this.closeModal();
          this.$router.push({
            name: 'DatasetDetail',
            params: { id: createdDataset.id }
          });
        } else if (createdDataset) {
          this.closeModal();
          this.$router.push({
            name: 'DatasetDetail',
            params: { id: createdDataset.id }
          });
        }
      } catch (err) {
        console.error(err);
        ErrorService.onError(err);
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>
